/* ---------------------------------------------------------------------------
   Offer tiers - one basket per tier, posted in a single request.

   Paired with blocks/hsp-offer-tiers.liquid. Classic script, not a module: the
   block can be re-rendered inside a section fetched by section-renderer.js, and a
   module would only evaluate once for the page. Everything re-reads the DOM.

   THIS FILE COMPUTES NOTHING. The block resolves every handle and sku to a variant
   id server-side and hands the finished basket over as data-items. No prices are
   summed here and no money is formatted here, because both already happened in
   Liquid where the catalogue is.

   WHY THE INTERCEPTION IS UNCONDITIONAL
   The earlier pack-tiers version let the native product form submit whenever the
   host tier was selected, because that tier really was the host product. Here every
   tier is a basket, including the first, so there is never a native path to stand
   aside for. The submit is caught at the document in the CAPTURE phase, ahead of
   product-form-component's own bubble-phase handler. That ordering is a DOM spec
   guarantee, not a load-order accident; assets/hp-variant-logic.js:30-39 relies on
   the same thing and says so.

   The fetch, the failure test and the cart refresh are lifted from
   assets/hsp-quick-add.js:605-665, which documents why each is shaped as it is.
   One difference: a multi-add response is an object carrying an items array, which
   is the shape assets/product-form.js:253-263 already looks for.
   --------------------------------------------------------------------------- */
(function () {
  if (window.__hspOfferTiersInit) return;
  window.__hspOfferTiersInit = true;

  var PAGE = '.hsp-product-page';

  function groups() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-hsp-offer]'));
  }

  function group(el) {
    return el.closest('[data-hsp-offer]');
  }

  function formFor(el) {
    var host = el.closest('.product-details') || document;
    return (
      host.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  /* Which rung is chosen, in falling order of authority.

     1. ?variant= in the URL, when it names a rung. Liquid already resolves this on a
        fresh page load, but a section morph re-runs init() against markup that may have
        been rendered without the parameter, and that is exactly when an ad click would
        silently lose the rung it paid for. Reading the URL here costs nothing and makes
        the answer the same every time.
     2. Whatever Liquid marked selected.
     3. The first rung, because the default may have been dropped for stock. */
  function selectedRow(g) {
    var want = null;
    try {
      want = new URL(window.location.href).searchParams.get('variant');
    } catch (e) {
      /* No URL, no preference. */
    }
    if (want) {
      var match = g.querySelector('[data-hsp-offer-tier][data-variant="' + want.replace(/"/g, '') + '"]');
      if (match) return match;
    }
    return g.querySelector('[data-hsp-offer-tier].is-selected') ||
           g.querySelector('[data-hsp-offer-tier]');
  }

  function basket(row) {
    try {
      return JSON.parse(row.getAttribute('data-items')) || [];
    } catch (e) {
      /* A malformed basket must not silently post something wrong. Returning
         empty makes submitMulti bail, which leaves the shopper on the page with
         a notice rather than an unexpected cart. */
      return [];
    }
  }

  function select(row) {
    var g = group(row);
    if (!g) return;

    Array.prototype.forEach.call(g.querySelectorAll('[data-hsp-offer-tier]'), function (r) {
      var on = r === row;
      r.classList.toggle('is-selected', on);
      r.setAttribute('aria-checked', on ? 'true' : 'false');
      /* Roving tabindex. Every rung is a <button>, so all of them were tabbable
         and a four-rung ladder cost four presses to step past. A radiogroup is
         meant to be ONE tab stop, with the arrow keys moving inside it - which is
         what the keydown handler below already provided. This is the other half
         of that contract, and without it the arrows and the Tab key disagree
         about where focus goes. */
      r.tabIndex = on ? 0 : -1;
    });

    syncPanel(g, row);
    syncTotal(g, row);
    syncStickyBar(g, row);
    syncUrl(row);
  }

  /* Re-read the quantity and restate every total. Separate from select() because
     nothing about the SELECTION changed — only the multiplier did. */
  function refreshTotals() {
    groups().forEach(function (g) {
      var row = g.querySelector('[data-hsp-offer-tier].is-selected') || selectedRow(g);
      if (!row) return;
      syncTotal(g, row);
      syncStickyBar(g, row);
    });
  }

  /* Put the chosen rung in the URL, when it has a variant of its own.

     A master bundle sells each rung as a variant of the same product, and an ad or a
     feed entry links straight at one with ?variant=<id>. Writing it back on every
     selection makes that a two-way street: the link chooses the rung, and choosing a
     rung makes the link. Sharing the page, going back, and a section morph re-reading
     the URL all then land on the same offer.

     replaceState, not pushState: picking between rungs is not navigation, and filling
     the back button with them would trap the shopper on the page.

     Silent on failure and skipped entirely for a v2 config, where no rung has a
     variant and there is nothing to name. */
  function syncUrl(row) {
    var variant = row.getAttribute('data-variant');
    if (!variant || !window.history || !history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      if (url.searchParams.get('variant') === variant) return;
      url.searchParams.set('variant', variant);
      history.replaceState(history.state, '', url.toString());
    } catch (e) {
      /* A URL the browser will not parse is not worth failing a selection over. */
    }
  }

  /* THE ONE PLACE THIS FILE FORMATS MONEY, AND WHY IT HAS TO.
     "Today you pay" has to answer for the QUANTITY SELECTOR, and two bundles is a
     number Liquid never rendered — it rendered one. Multiplying is arithmetic on
     cents, and cents have to be turned back into a string somewhere.

     The rule the rest of the file follows still holds, though, because this does
     not invent a format: it reads the shop's own `money_format`, which is the
     same setting the `money` filter uses, and applies the same substitutions
     Shopify's own formatter does. Any other approach — a hard-coded "$", or
     Intl.NumberFormat guessing from the currency code — is a second opinion about
     how this shop writes money, and would drift the moment a merchant edits the
     format or the theme is used on a store that is not American.

     Tags are stripped because money_format may carry markup and the total is set
     with textContent. */
  var MONEY_RE = /\{\{\s*(\w+)\s*\}\}/;

  function group3(intStr, sep) {
    return intStr.replace(/(\d)(?=(\d\d\d)+$)/g, '$1' + sep);
  }

  function formatMoney(cents, format) {
    var match = String(format || '').match(MONEY_RE);
    if (!match) return null;

    var placeholder = match[1];
    var decimals = placeholder.indexOf('no_decimals') === -1 ? 2 : 0;
    var thousands = ',';
    var decimalMark = '.';

    if (placeholder.indexOf('comma_separator') > -1) { thousands = '.'; decimalMark = ','; }
    else if (placeholder.indexOf('apostrophe_separator') > -1) { thousands = "'"; }
    else if (placeholder.indexOf('period_and_space_separator') > -1) { thousands = ' '; decimalMark = '.'; }
    else if (placeholder.indexOf('space_separator') > -1) { thousands = ' '; decimalMark = ','; }

    var value = (Math.round(cents) / 100).toFixed(decimals);
    var parts = value.split('.');
    var out = group3(parts[0], thousands);
    if (parts[1]) out += decimalMark + parts[1];

    return String(format).replace(MONEY_RE, out).replace(/<[^>]*>/g, '').trim();
  }

  /* What "Today you pay" should read, and the note beside the label.

     At quantity 1 nothing is computed at all — the row's own Liquid-rendered
     string is copied, exactly as before. Above 1, the formatter has to earn it:
     it re-renders the UNIT price from cents and compares that against the string
     Liquid actually printed. If the two disagree, this shop formats money in a
     way the substitutions above do not cover, and rather than print a plausible
     wrong number the total falls back to "3 x $119.95" — unambiguous, and made
     only of figures Liquid produced. */
  function totals(g, row) {
    var money = row.querySelector('.hsp-offer__now');
    var unitText = money ? money.textContent.trim() : '';
    var qty = bundleQty(g);
    if (qty <= 1) return {value: unitText, note: ''};

    var cents = parseInt(row.getAttribute('data-total-cents'), 10);
    var format = g.getAttribute('data-money-format');

    if (isFinite(cents) && format && formatMoney(cents, format) === unitText) {
      return {value: formatMoney(cents * qty, format), note: qty + ' × ' + unitText};
    }
    return {value: qty + ' × ' + unitText, note: ''};
  }

  function syncTotal(g, row) {
    var sums = totals(g, row);
    var out = g.querySelector('[data-hsp-offer-total]');
    var note = g.querySelector('[data-hsp-offer-note]');
    if (out && sums.value) out.textContent = sums.value;
    if (note) {
      note.textContent = sums.note;
      note.hidden = !sums.note;
    }
  }

  /* The extras panel sits outside the rows, one per bundle, and only the selected
     one is shown. It is a sibling rather than a child because a row is a <button>
     and interactive content cannot nest inside one - and because Tuck puts the
     same list below the ladder, where it reads as what you are getting rather than
     as more text on an option you have not chosen.

     Toggled with .hidden, not style.display, so it stays in step with the `hidden`
     attribute Liquid renders on first paint. A bundle with only a main product has
     no panel at all, which is why every step here is optional. */
  function syncPanel(g, row) {
    var want = row.getAttribute('data-bundle');
    Array.prototype.forEach.call(g.querySelectorAll('[data-hsp-offer-panel]'), function (p) {
      p.hidden = p.getAttribute('data-hsp-offer-panel') !== want;
    });
  }

  /* The sticky bar mirrors the host product, which on a landing page is not what
     any tier adds. It syncs itself on variant:update, and the picker is hidden so
     that never fires - so rewrite it from the chosen tier instead. Every step is
     optional: a missing element just means that part stays as rendered. */
  function syncStickyBar(g, row) {
    var bar = document.querySelector('sticky-add-to-cart');
    if (!bar) return;

    var title = bar.querySelector('.sticky-add-to-cart__title');
    var price = bar.querySelector('.sticky-add-to-cart__price');
    var name = row.querySelector('.hsp-offer__name');

    if (title && name) title.textContent = name.textContent.trim();
    /* The same figure as "Today you pay", quantity included. The bar's button
       submits the same form, so showing the price of one bundle while the button
       adds three would be the bar lying about what it is about to do. */
    if (price) price.textContent = totals(g, row).value;
  }

  function collectCartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (node) {
      var id = node.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;
    var go = function () { if (typeof drawer.open === 'function') drawer.open(); };
    /* cart-drawer.js:52 gates its own handler on an "event instanceof CartAddEvent"
       test, and CartAddEvent is an ES-module class a classic script cannot
       construct, so a plain CustomEvent will never open the drawer. Open directly. */
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('cart-drawer-component').then(go);
    } else {
      go();
    }
  }

  function errorMessage(data) {
    if (!data) return null;
    return data.description || data.message || null;
  }

  function notice(g, text) {
    var el = g.querySelector('[data-hsp-offer-notice]');
    if (!el) return;
    if (text) { el.textContent = text; el.hidden = false; }
    else { el.textContent = ''; el.hidden = true; }
  }

  /* The theme's quantity selector sits directly above Add to cart and means "how
     many of this", which for a bundle is how many BUNDLES. Read at submit time, not
     cached: the shopper can change it after choosing a bundle, and a stale multiplier
     would silently add a different basket than the one on screen. */
  function bundleQty(g) {
    var form = formFor(g);
    var input = form && form.querySelector('input[name="quantity"]');
    var n = input ? parseInt(input.value, 10) : 1;
    return isFinite(n) && n > 0 ? n : 1;
  }

  /* Shopify accepts at most 5 product/order discount codes on one order. A bundle uses
     one or two, so this only bites if a shopper is already carrying several of their
     own - but it bites at CHECKOUT, which is the worst place to find out. */
  var MAX_CODES = 5;

  function parseCodes(row) {
    return (row.getAttribute('data-codes') || '')
      .split(',')
      .map(function (c) { return c.trim().toUpperCase(); })
      .filter(Boolean);
  }

  /* Did the bundle actually come out cheaper where it promised to?

     NOT "did every code report itself applicable". A code is ALSO reported
     inapplicable when something else already discounted the same line — an automatic
     rule, or a promo the shopper typed in — and in that case the price on screen is
     correct and a warning would be a lie. Measured: with the standing "free cushions"
     automatic rule active, BUNDLE-10-SPARE-EAR-CUSHIONS-FREE returns
     applicable:false while the cushions still land at $0.00 and the cart total
     matches the advertised figure exactly.

     So watch the PRICES, not the codes. data-discounted lists the variants the block
     said would be cheaper than list; if every one of them is, the bundle is priced,
     whatever mechanism got it there. */
  function pricedCorrectly(row, cart) {
    var expect = (row.getAttribute('data-discounted') || '')
      .split(',')
      .map(function (v) { return v.trim(); })
      .filter(Boolean);
    if (!expect.length) return true;

    var items = (cart && cart.items) || [];
    return expect.every(function (variantId) {
      return items.some(function (i) {
        var id = String(i.variant_id || i.id);
        return id === variantId && i.final_line_price < i.original_line_price;
      });
    });
  }

  /* Apply the selected bundle's discount codes, and return the resulting cart.
     Three things this has to get right, each learned from a measured cart:

     1. MERGE, never replace. The `discount` parameter sets the whole list, so posting
        only the bundle's codes silently drops a promo the shopper typed in themselves.
        assets/cart-discount.js:70 merges for the same reason.
     2. Ask for SECTIONS here. This response is rendered after the discounts land, so
        the drawer opens already showing struck prices; the add response was rendered
        before they existed. It is also a full cart object, which is why no separate
        /cart.js call follows.
     3. Read `applicable`. /cart/update.js returns 200 and stores codes it will not
        honour, marking them applicable:false with no error field anywhere. A 200 is
        not proof the price is right. */
  function applyCodes(g, row) {
    var wanted = parseCodes(row);

    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .catch(function () { return null; })
      .then(function (cart) {
        if (!wanted.length) return cart;

        var merged = ((cart && cart.discount_codes) || [])
          .map(function (d) { return (d.code || '').toUpperCase(); })
          .filter(Boolean);

        wanted.forEach(function (code) {
          if (merged.indexOf(code) === -1) merged.push(code);
        });

        if (merged.length > MAX_CODES) {
          notice(g, 'This cart already has too many discount codes for the bundle price to apply. Remove one at checkout.');
        }

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            discount: merged.join(','),
            sections: collectCartSectionIds().join(','),
            sections_url: window.location.pathname
          })
        })
          .then(function (res) { return res.json(); })
          .then(function (updated) {
            if (!pricedCorrectly(row, updated)) {
              /* Almost always one of: the generated code was never created, it has
                 been retired, or an automatic discount with combinations switched off
                 is suppressing every code on the cart. */
              notice(g, 'The bundle price could not be applied. Your items are in the cart at full price.');
            }
            return updated;
          })
          .catch(function () {
            /* The items are already in the cart. Losing the discount is bad; throwing
               away a successful add on top of it is worse. */
            notice(g, 'The bundle price could not be applied. Your items are in the cart at full price.');
            return cart;
          });
      });
  }

  function submitMulti(g) {
    var row = selectedRow(g);
    if (!row) return;

    var mult = bundleQty(g);
    var items = basket(row).map(function (item) {
      return {
        id: item.id,
        quantity: item.quantity * mult,
        properties: item.properties
      };
    });
    if (!items.length) { notice(g, 'Could not read that option. Please refresh and try again.'); return; }

    g.setAttribute('data-hsp-offer-busy', 'true');
    notice(g, '');

    var payload = {
      items: items,
      sections: collectCartSectionIds().join(','),
      sections_url: window.location.pathname
    };

    /* Literal path, not Theme.routes.cart_add_url: Theme is declared with const in
       a classic inline script (snippets/scripts.liquid:305), so it is a lexical
       global rather than a property of window. */
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        return res.json().catch(function () { return null; }).then(function (data) {
          /* Shopify signals failure with 4xx OR a 200 body carrying status/errors. */
          var failed = !res.ok || (data && (data.status || data.errors));
          if (failed) throw new Error(errorMessage(data) || 'Could not add to cart');
          return data;
        });
      })
      .then(function (added) {
        /* THE SECOND HALF OF THE PRICE. The add put the right items in the cart at
           full price; these codes are what make the advertised number true. The store
           is not Plus, so a discount Function - which would read the bundle config at
           checkout and need none of this - is unavailable. See BUNDLES.md.

           Sections are requested HERE, not on the add: /cart/add.js renders them
           before any discount exists, and component-cart-items.js morphs from
           event.detail.data.sections whenever they are present. Handing it the add's
           sections would open the drawer showing full prices, with the real ones
           arriving only if something else forced a re-render. Asking /cart/update.js
           for them instead makes the returned markup already discounted, and drops
           the separate /cart.js round trip: the update response IS a full cart. */
        return applyCodes(g, row).then(function (cart) {
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart || added,
              sourceId: 'hsp-offer-tiers',
              data: {
                source: 'hsp-offer-tiers',
                /* A TOTAL, not a delta: cart-icon.js:56-61 assigns rather than
                   increments whenever source is not 'product-form-component'. */
                itemCount: cart ? cart.item_count : null,
                sections: (cart && cart.sections) || (added && added.sections)
              }
            }
          }));
          openCartDrawer();
        });
      })
      .catch(function (error) {
        notice(g, error.message || 'Could not add to cart.');
      })
      .then(function () {
        g.removeAttribute('data-hsp-offer-busy');
      });
  }

  /* --- Bindings ---------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    var row = event.target.closest && event.target.closest('[data-hsp-offer-tier]');
    if (!row) return;
    event.preventDefault();
    select(row);
  });

  /* THE QUANTITY SELECTOR, WHICH DOES NOT FIRE THE EVENTS YOU WOULD EXPECT.
     Its + and - buttons assign to input.value in JavaScript
     (assets/component-quantity-selector.js:updateQuantity), and a programmatic
     assignment fires neither `input` nor `change`. Listening for those alone
     caught typing and missed every button press, which is exactly the case a
     shopper uses.

     What it does fire is `quantity-selector:update`, bubbling, declared at
     assets/events.js:20. That is the real signal. `input` and `change` stay
     bound as well, for a typed value and for any other theme's selector that
     has no such event.

     All three only restate a number. The quantity that actually reaches the cart
     is read from the input at submit time, as it always was. */
  document.addEventListener('quantity-selector:update', refreshTotals);

  document.addEventListener('input', function (event) {
    var el = event.target;
    if (el && el.name === 'quantity') refreshTotals();
  });

  document.addEventListener('change', function (event) {
    var el = event.target;
    if (el && el.name === 'quantity') refreshTotals();
  });

  /* Arrow keys move between rungs, Home and End jump to the ends. All four are
     what a radiogroup is expected to answer, and the ladder is one: a shopper on
     a keyboard tabs to it once and then chooses inside it.

     Left and Right are bound as well as Up and Down. The group is a vertical
     list, so the vertical pair is the obvious one, but the ARIA pattern gives a
     radiogroup both and a screen reader user may reach for either. */
  var STEP = {ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1};

  document.addEventListener('keydown', function (event) {
    var isStep = Object.prototype.hasOwnProperty.call(STEP, event.key);
    var isEnd = event.key === 'Home' || event.key === 'End';
    if (!isStep && !isEnd) return;

    var row = event.target.closest && event.target.closest('[data-hsp-offer-tier]');
    if (!row) return;
    var g = group(row);
    if (!g) return;

    var rows = Array.prototype.slice.call(g.querySelectorAll('[data-hsp-offer-tier]'));
    var next = isEnd
      ? rows[event.key === 'Home' ? 0 : rows.length - 1]
      : rows[rows.indexOf(row) + STEP[event.key]];

    /* No wrap-around, and no move when there is nowhere to go. Wrapping from the
       cheapest rung to the dearest one is a surprise a shopper pays for. */
    if (!next || next === row) return;

    event.preventDefault();
    next.focus();
    select(next);
  });

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;

      var g = groups().filter(function (x) { return formFor(x) === form; })[0];
      if (!g) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (g.getAttribute('data-hsp-offer-busy') === 'true') return;
      submitMulti(g);
    },
    true // capture: ahead of product-form-component's own handler
  );

  function init() {
    groups().forEach(function (g) {
      /* Flag the page as soon as a tier ladder exists. The host's variant picker,
         price and savings badge all describe a product no tier adds, so they are
         hidden from first paint rather than on first click. */
      var page = document.querySelector(PAGE);
      if (page) page.setAttribute('data-hsp-tier', 'offer');

      /* select(), not just a sync: the block marks whichever bundle carries
         "default": true, but that bundle may have been dropped for stock or
         disabled, in which case NO row is marked. selectedRow() falls back to the
         first, and going through select() is what actually applies is-selected to
         it — otherwise the ladder renders with nothing chosen and the first click
         on Add to cart posts a bundle the shopper never picked. */
      var row = selectedRow(g);
      if (row) select(row);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
